import { toast } from 'sonner'
import { Button } from 'tailwind-v4/components/button'
import { cn } from '@/lib/utils'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import { DateTimeLabel } from '@/components/ui/date-time-label'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import type { PendingInvitationType } from '@/state/morpheus/types/invite/pending-invitation.types'

function inviterLabel(invited_by: PendingInvitationType['invited_by']): string {
  const name = [invited_by.first, invited_by.last].filter(Boolean).join(' ')
  return name || invited_by.email
}

function InvitationRow({ invitation }: { invitation: PendingInvitationType }) {
  const [respond, { isLoading }] = morpheusApi.useRespondToInvitationMutation()

  const onAccept = () => {
    toast.promise(respond({ id: invitation.id, action: 'accept' }).unwrap(), {
      loading: `Joining ${invitation.organization.name}…`,
      success: () => {
        window.location.reload()
        return `You've joined ${invitation.organization.name}`
      },
      error: `Couldn't accept the invitation — it may no longer be valid`,
    })
  }

  const onDecline = () => {
    toast.promise(respond({ id: invitation.id, action: 'delete' }).unwrap(), {
      loading: `Declining invitation…`,
      success: `Invitation declined`,
      error: `Couldn't decline the invitation — it may no longer be valid`,
    })
  }

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-foreground">
          {invitation.organization.name}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {invitation.role.name} · Invited by{' '}
          {inviterLabel(invitation.invited_by)}
          {invitation.locations.length > 0
            ? ` · ${invitation.locations.join(', ')}`
            : null}
          {' · '}
          <DateTimeLabel
            date={invitation.invited_at}
            format={TimeFormats.StandardDateMonthYear}
          />
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={isLoading}
          onClick={onDecline}
        >
          Decline
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={isLoading}
          onClick={onAccept}
        >
          Accept
        </Button>
      </div>
    </div>
  )
}

/**
 * `className` is the host page's business: a `Page bleed` scroller has no mobile
 * gutter (so its table can reach the screen edge), and this bordered list would
 * otherwise sit flush against it — pass `px-4 @2xl:px-0`. It goes on the root
 * rather than a wrapper the caller adds, so a section with no invitations can't
 * leave a ghost `gap` in the page's flex column.
 */
export function PendingInvitationsSection({
  className,
}: {
  className?: string
}) {
  const { data: invitations, isLoading } =
    morpheusApi.useGetPendingInvitationsQuery()

  if (isLoading || !invitations || invitations.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <h2 className="text-sm font-medium text-foreground">
        Pending invitations
      </h2>
      <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
        {invitations.map((invitation) => (
          <InvitationRow key={invitation.id} invitation={invitation} />
        ))}
      </div>
    </div>
  )
}
